import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  App,
  Button,
  DatePicker,
  Drawer,
  Input,
  Select,
  Space,
  Spin,
  Timeline,
  Tree,
  Popconfirm,
  Modal,
} from 'antd';
import { useParams } from 'react-router-dom';
import {
  AppstoreAddOutlined,
  ApartmentOutlined,
  CloseOutlined,
  HistoryOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { buildComponentTree } from 'pages/liftlogic/v2/Components/buildComponentTree';
import { buildIndex } from 'pages/liftlogic/v2/Components/treeIndex';
import AttributeForm from 'pages/liftlogic/v2/Components/AttributeForm';
import { attrsForType } from 'pages/liftlogic/v2/Components/attrsForType';
import ComponentTreeRow from 'pages/liftlogic/v2/Components/ComponentTreeRow';
import ComponentToolbar from 'pages/liftlogic/v2/Components/ComponentToolbar';
import ComponentInspector from 'pages/liftlogic/v2/Components/ComponentInspector';
import ComponentMapView from 'pages/liftlogic/v2/Components/ComponentMapView';
import {
  presignComponentAttrImageUpload,
  confirmComponentAttrImage,
  getComponentAttrImageUrl,
} from 'api/componentAttributeImage';
import { getComponentsHealth } from 'api/componentHealth';
import 'pages/liftlogic/v2/Components/componentTree.css';

const SPLIT_MIN_WIDTH = 760; // ancho de contenedor a partir del cual el inspector va en split

function ComponentInstanceTree({
  api,
  defsApi,
  templatesApi,
  typesApi,
  canEdit = false,
  equipoId: equipoIdProp,
  splitInspector = false,
}) {
  const params = useParams();
  const equipoId = equipoIdProp ?? params.equipoId;
  const { message, modal } = App.useApp();

  const rootRef = useRef(null);

  const [treeData, setTreeData] = useState([]);
  const [rawRoots, setRawRoots] = useState([]);
  const [defs, setDefs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [healthMap, setHealthMap] = useState({});

  // estado de vista
  const [view, setView] = useState('lista');
  const [q, setQ] = useState('');
  const [density, setDensity] = useState('comoda');
  const [guides, setGuides] = useState(true);
  const [connector, setConnector] = useState('curvo');
  const [fullscreen, setFullscreen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({});
  const [wide, setWide] = useState(true);

  // Estado para el Drawer de historial
  const [historyNode, setHistoryNode] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Estado para el Drawer de reemplazo
  const [replaceNode, setReplaceNode] = useState(null);
  const [replaceDraft, setReplaceDraft] = useState({});
  const [replaceReason, setReplaceReason] = useState('desgaste');
  const [replaceNote, setReplaceNote] = useState('');
  const [replaceDate, setReplaceDate] = useState(dayjs());

  // índice para path/breadcrumb
  const idx = useMemo(() => buildIndex(rawRoots), [rawRoots]);
  const allKeys = useMemo(() => Object.keys(idx.byId).map((k) => Number(k)), [idx]);

  const loadTree = useCallback(async () => {
    if (!api || !equipoId) return;
    setLoading(true);
    try {
      const d = await api.getTree(Number(equipoId));
      setRawRoots(d.tree || []);
      setTreeData(buildComponentTree(d.tree));
    } catch (e) {
      message.error('No se pudo cargar el árbol');
    } finally {
      setLoading(false);
    }
  }, [api, equipoId, message]);

  const loadDefs = useCallback(async () => {
    if (!defsApi) return;
    try {
      const d = await defsApi.list();
      setDefs(d.defs || []);
    } catch (e) {
      /* opcional */
    }
  }, [defsApi]);

  const loadTypes = useCallback(async () => {
    if (!typesApi) return;
    try {
      const d = await typesApi.list();
      setTypes(d.types || []);
    } catch (e) {
      /* opcional */
    }
  }, [typesApi]);

  const loadHealth = useCallback(async () => {
    if (!equipoId) return;
    try {
      const d = await getComponentsHealth(Number(equipoId));
      setHealthMap(d.health || {});
    } catch (e) {
      // La salud es best-effort: si falla, el árbol sigue funcionando sin dots de salud real
      setHealthMap({});
    }
  }, [equipoId]);

  useEffect(() => {
    loadTree();
    loadDefs();
    loadTypes();
  }, [loadTree, loadDefs, loadTypes]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  // expandir todo por defecto solo en la primera carga con datos
  const didInitExpand = useRef(false);
  useEffect(() => {
    if (didInitExpand.current) return;
    if (allKeys.length) {
      setExpandedKeys(allKeys);
      didInitExpand.current = true;
    }
  }, [allKeys]);

  // medir ancho del contenedor → split vs drawer
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWide(el.clientWidth >= SPLIT_MIN_WIDTH));
    ro.observe(el);
    setWide(el.clientWidth >= SPLIT_MIN_WIDTH);
    return () => ro.disconnect();
  }, []);

  // búsqueda → expandir ramas con match
  useEffect(() => {
    if (!q) return;
    const ql = q.toLowerCase();
    setExpandedKeys((prev) => {
      const open = new Set(prev);
      allKeys.forEach((id) => {
        if ((idx.byId[id]?.name || '').toLowerCase().includes(ql)) {
          idx.pathOf(id).forEach((p) => open.add(p.id));
        }
      });
      return Array.from(open);
    });
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const expandAll = () => setExpandedKeys(allKeys);
  const collapseAll = () => setExpandedKeys(rawRoots.map((r) => r.id));

  // ---- mutaciones (mismas que antes) ----
  const addChild = (parent) => {
    const nameRef = { current: '' };
    const nodeTypeRef = { current: null };
    modal.confirm({
      title: `Agregar bajo "${parent?.title || 'raíz'}"`,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Motor de izaje"
            autoFocus
            onChange={(e) => {
              nameRef.current = e.target.value;
            }}
          />
          {types.length > 0 && (
            <Select
              allowClear
              placeholder="Tipo de componente (opcional)"
              style={{ width: '100%' }}
              options={types.map((t) => ({ value: t.key, label: t.label }))}
              onChange={(v) => {
                nodeTypeRef.current = v ?? null;
              }}
            />
          )}
        </Space>
      ),
      onOk: async () => {
        try {
          await api.addNode(Number(equipoId), {
            name: nameRef.current || 'Nuevo',
            parent_id: parent ? parent.key : null,
            ...(nodeTypeRef.current != null ? { node_type: nodeTypeRef.current } : {}),
          });
          loadTree();
        } catch (e) {
          message.error('No se pudo agregar el nodo');
        }
      },
    });
  };

  const renameNode = (node) => {
    let name = node.title;
    modal.confirm({
      title: 'Renombrar',
      content: (
        <Input
          defaultValue={node.title}
          onChange={(e) => {
            name = e.target.value;
          }}
        />
      ),
      onOk: async () => {
        try {
          await api.updateNode(node.key, { name });
          loadTree();
        } catch (e) {
          message.error('No se pudo renombrar el nodo');
        }
      },
    });
  };

  const deleteNode = (node) => {
    modal.confirm({
      title: '¿Eliminar nodo y descendientes?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.removeNode(node.key);
          message.success('Eliminado');
          loadTree();
        } catch (e) {
          message.error('No se pudo eliminar');
        }
      },
    });
  };

  const cloneTemplate = async () => {
    try {
      const { roots } = await templatesApi.listRoots();
      let rootId = roots?.[0]?.id ?? null;
      modal.confirm({
        title: 'Clonar plantilla',
        content: (
          <Select
            style={{ width: '100%' }}
            defaultValue={rootId}
            onChange={(v) => {
              rootId = v;
            }}
            options={(roots || []).map((r) => ({ value: r.id, label: r.name }))}
          />
        ),
        onOk: async () => {
          if (!rootId) return;
          try {
            await api.cloneTemplate(Number(equipoId), rootId);
            message.success('Plantilla clonada');
            loadTree();
          } catch (e) {
            message.error('No se pudo clonar la plantilla');
          }
        },
      });
    } catch (e) {
      message.error('No se pudieron cargar las plantillas');
    }
  };

  const onDrop = async (info) => {
    if (!canEdit) return;
    const newParent = info.dropToGap ? info.node.raw.parent_id ?? null : info.node.key;
    try {
      await api.updateNode(info.dragNode.key, { parent_id: newParent });
      loadTree();
    } catch (e) {
      message.error('No se pudo mover');
    }
  };

  // ---- selección / inspector ----
  const selectNode = (node) => {
    const id = node.key ?? node.id; // acepta treeData node o raw node (del mapa)
    setSelectedId(id);
    setDraft(idx.byId[id]?.attributes || {});
  };
  const closeInspector = () => setSelectedId(null);

  const saveAttrs = async () => {
    try {
      await api.updateNode(selectedId, { attributes: draft });
      message.success('Atributos guardados');
      loadTree();
    } catch (e) {
      message.error(e?.response?.data?.message || 'No se pudo guardar');
    }
  };

  const openReplace = (node) => {
    setReplaceNode(node);
    setReplaceDraft(node.raw?.attributes || {});
    setReplaceReason('desgaste');
    setReplaceNote('');
    setReplaceDate(dayjs());
  };

  const openHistory = (node) => {
    setHistoryNode(node);
    setHistoryLoading(true);
    api
      .listReplacements(node.key)
      .then((d) => setHistory(d.replacements || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  const submitReplace = async () => {
    try {
      await api.replaceComponent(replaceNode.key, {
        new: { attributes: replaceDraft },
        reason: replaceReason,
        reason_note: replaceNote || null,
        replaced_at: replaceDate.format('YYYY-MM-DD'),
      });
      message.success('Componente reemplazado');
      setReplaceNode(null);
      loadTree();
    } catch (e) {
      message.error(e?.response?.data?.message || 'No se pudo reemplazar');
    }
  };

  const selRaw = selectedId != null ? idx.byId[selectedId] : null;
  const selTreeNode = selRaw ? { key: selRaw.id, title: selRaw.name, raw: selRaw } : null;
  const selPath = selectedId != null ? idx.pathOf(selectedId) : [];

  const imageApi = useMemo(() => {
    if (!equipoId || selectedId == null) return null;
    const nodeId = selectedId;
    return {
      upload: async (attrKey, file) => {
        const { uploadUrl, key } = await presignComponentAttrImageUpload(
          equipoId,
          nodeId,
          attrKey,
          file.type
        );
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        await confirmComponentAttrImage(equipoId, nodeId, attrKey, key);
        return key;
      },
      getUrl: async (attrKey) => {
        const { url } = await getComponentAttrImageUrl(equipoId, nodeId, attrKey);
        return url;
      },
    };
  }, [equipoId, selectedId]);

  const ql = q.toLowerCase();
  const titleRender = (node) => (
    <ComponentTreeRow
      node={node}
      typesCatalog={types}
      canEdit={canEdit}
      matched={!!q && (node.title || '').toLowerCase().includes(ql)}
      health={healthMap[node.key]}
      onAdd={addChild}
      onAttrs={selectNode}
      onRename={renameNode}
      onDelete={deleteNode}
      onReplace={canEdit && api.replaceComponent ? openReplace : undefined}
      onHistory={api.listReplacements ? openHistory : undefined}
    />
  );

  const inspectorNode = (floating, showClose = true) => (
    <ComponentInspector
      node={selTreeNode}
      path={selPath}
      defs={defs}
      values={draft}
      canEdit={canEdit}
      imageApi={imageApi}
      onChange={setDraft}
      onSave={saveAttrs}
      onClose={closeInspector}
      floating={floating}
      showClose={showClose}
      equipoId={equipoId}
      health={healthMap[selTreeNode?.key]}
      onHealthChanged={loadHealth}
    />
  );

  // Split solo en la página dedicada ancha (splitInspector). En montajes
  // embebidos (tab del técnico, Drawer del equipo) el inspector va en Drawer
  // para que el árbol use todo el ancho.
  const showSplit = splitInspector && wide && view === 'lista';

  return (
    <div
      className={
        'cmp-tree-root' + (guides ? ' guided' : '') + (density === 'dense' ? ' dense' : '')
      }
      ref={rootRef}
    >
      <ComponentToolbar
        view={view}
        onView={setView}
        q={q}
        onQ={setQ}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
        onFullscreen={() => setFullscreen(true)}
        density={density}
        onDensity={setDensity}
        guides={guides}
        onGuides={setGuides}
        connector={connector}
        onConnector={setConnector}
      />

      {canEdit && view === 'lista' && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Button icon={<AppstoreAddOutlined />} onClick={() => addChild(null)}>
            Agregar nodo raíz
          </Button>
          <Button icon={<ApartmentOutlined />} onClick={cloneTemplate}>
            Clonar plantilla
          </Button>
        </Space>
      )}

      <div className="cmp-body">
        <div className="cmp-tree-area" style={view === 'mapa' ? { height: 480 } : undefined}>
          {view === 'lista' ? (
            <>
              {treeData.length === 0 && !loading && (
                <div style={{ opacity: 0.6, marginBottom: 12 }}>
                  Árbol vacío{canEdit ? ' — agregá nodos o cloná una plantilla.' : '.'}
                </div>
              )}
              <Tree
                draggable={canEdit}
                blockNode
                showLine={guides ? { showLeafIcon: false } : false}
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                selectedKeys={selectedId != null ? [selectedId] : []}
                onSelect={(keys, info) => selectNode(info.node)}
                onDrop={canEdit ? onDrop : undefined}
                titleRender={titleRender}
                disabled={loading}
              />
            </>
          ) : (
            <ComponentMapView
              roots={rawRoots}
              selectedId={selectedId}
              onSelect={selectNode}
              connector={connector}
              fullscreen={false}
              healthMap={healthMap}
            />
          )}
        </div>

        {showSplit && inspectorNode(false)}
      </div>

      {/* inspector en Drawer cuando NO hay split (contenedor angosto o vista mapa inline) */}
      {!showSplit && (
        <Drawer
          title={selTreeNode ? `Atributos — ${selTreeNode.title}` : 'Atributos'}
          open={selectedId != null}
          onClose={closeInspector}
          width={420}
          styles={{ body: { padding: 0 } }}
        >
          {inspectorNode(false, false)}
        </Drawer>
      )}

      {/* fullscreen del mapa */}
      {fullscreen && (
        <div className="cmp-fs">
          <div className="cmp-fs-bar">
            <span style={{ fontWeight: 600 }}>Mapa de componentes</span>
            <span style={{ flex: 1 }} />
            <Button icon={<CloseOutlined />} onClick={() => setFullscreen(false)}>
              Cerrar
            </Button>
          </div>
          <div className="cmp-fs-main">
            <ComponentMapView
              roots={rawRoots}
              selectedId={selectedId}
              onSelect={selectNode}
              connector={connector}
              fullscreen
              healthMap={healthMap}
            />
            {selTreeNode && inspectorNode(true)}
          </div>
        </div>
      )}

      <Drawer
        title={`Reemplazar — ${replaceNode?.title || ''}`}
        open={!!replaceNode}
        onClose={() => setReplaceNode(null)}
        width={480}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setReplaceNode(null)}>Cancelar</Button>
            <Button type="primary" onClick={submitReplace}>
              Guardar reemplazo
            </Button>
          </Space>
        }
      >
        {replaceNode && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Atributos del componente nuevo</div>
              <AttributeForm
                defs={attrsForType(defs, replaceNode?.raw?.node_type ?? null)}
                values={replaceDraft}
                onChange={setReplaceDraft}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Motivo</div>
              <Select
                style={{ width: '100%' }}
                value={replaceReason}
                onChange={setReplaceReason}
                options={[
                  { value: 'desgaste', label: 'Desgaste' },
                  { value: 'falla', label: 'Falla' },
                  { value: 'vida_util_cumplida', label: 'Vida útil cumplida' },
                  { value: 'otro', label: 'Otro' },
                ]}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Nota (opcional)</div>
              <Input.TextArea value={replaceNote} onChange={(e) => setReplaceNote(e.target.value)} />
            </div>
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Fecha de reemplazo</div>
              <DatePicker value={replaceDate} onChange={setReplaceDate} />
            </div>
          </Space>
        )}
      </Drawer>

      <Drawer
        title={`Historial — ${historyNode?.title || ''}`}
        open={!!historyNode}
        onClose={() => setHistoryNode(null)}
        width={480}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setHistoryNode(null)}>Cerrar</Button>
          </Space>
        }
      >
        {historyLoading ? (
          <Spin />
        ) : (
          <>
            {historyNode && history.length === 0 && (
              <div style={{ opacity: 0.5 }}>Sin reemplazos registrados</div>
            )}
            {historyNode && (
              <Timeline>
                <Timeline.Item color="green">
                  <div>
                    <strong>Actual</strong>
                    {' — '}
                    {historyNode.raw?.name}
                    {historyNode.raw?.node_type && (
                      <span style={{ opacity: 0.6 }}>{` [${historyNode.raw.node_type}]`}</span>
                    )}
                  </div>
                  {historyNode.raw?.attributes &&
                    Object.entries(historyNode.raw.attributes).map(([k, v]) => (
                      <div key={k} style={{ fontSize: 12 }}>
                        <span style={{ opacity: 0.7 }}>{k}:</span> {String(v)}
                      </div>
                    ))}
                  {historyNode.raw?.installed_at && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ opacity: 0.7 }}>Instalado:</span>{' '}
                      {historyNode.raw.installed_at}
                    </div>
                  )}
                </Timeline.Item>
                {history.map((r, idx) => (
                  <Timeline.Item key={idx}>
                    <div>
                      <strong>{r.name_snapshot}</strong>
                      {r.node_type_snapshot && (
                        <span style={{ opacity: 0.6 }}>{` [${r.node_type_snapshot}]`}</span>
                      )}
                    </div>
                    {r.attributes_snapshot &&
                      Object.entries(r.attributes_snapshot).map(([k, v]) => (
                        <div key={k} style={{ fontSize: 12 }}>
                          <span style={{ opacity: 0.7 }}>{k}:</span> {String(v)}
                        </div>
                      ))}
                    <div style={{ fontSize: 12 }}>
                      {r.installed_at} → {r.removed_at}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      Motivo: {r.reason}
                      {r.reason_note ? ` — ${r.reason_note}` : ''}
                    </div>
                    <div style={{ fontSize: 12 }}>por {r.replaced_by}</div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

export default ComponentInstanceTree;
